import { useEffect, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link } from "react-router-dom";
import { Coins, ShoppingBag, Loader2, Plus, Minus, RotateCcw, UserCheck, Gift } from "lucide-react";
import api from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const STATUS_COLOR = { active: "#22C55E", refunded: "#66C2FF", completed: "#737373", expired: "#FF4500", paused: "#FF9F1C", cancelled: "#737373" };
const EVENT = {
  purchase: { Icon: Plus, color: "#22C55E" },
  used: { Icon: Minus, color: "#FF9F1C" },
  refund: { Icon: RotateCcw, color: "#66C2FF" },
  coach_credit: { Icon: UserCheck, color: "#CCFF00" },
  gift: { Icon: Gift, color: "#CCFF00" },
  removed: { Icon: Minus, color: "#FF9F1C" },
  re_added: { Icon: UserCheck, color: "#22C55E" },
  rescheduled: { Icon: RotateCcw, color: "#A3A3A3" },
  rescheduled_into: { Icon: RotateCcw, color: "#CCFF00" },
};

function PackRow({ p }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 py-3 border-b border-[#1F1F1F] text-sm" data-testid={`credit-pack-${p.id}`}>
      <div className="col-span-2 sm:col-span-1">
        <div className="font-semibold inline-flex items-center gap-1.5" data-testid={`credit-pack-name-${p.id}`}>
          {p.is_gift && <Gift className="w-3.5 h-3.5 text-[#CCFF00] shrink-0" />}{p.name}
        </div>
        {p.is_gift && <div className="text-[10px] uppercase tracking-wider text-[#CCFF00]" data-testid={`credit-pack-gift-${p.id}`}>Gift{p.gift_reason ? ` · ${p.gift_reason}` : ""}</div>}
      </div>
      <div><span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: STATUS_COLOR[p.status] || "#A3A3A3" }} data-testid={`credit-pack-status-${p.id}`}>{p.status}</span></div>
      <div className="text-[#A3A3A3]">{p.is_gift ? "Gifted" : "Bought"} {fmtDate(p.purchased_at)}</div>
      <div className="text-[#A3A3A3]">{p.expires_at ? `Expires ${fmtDate(p.expires_at)}` : "No expiry"}</div>
      <div className="font-mono" data-testid={`credit-pack-remaining-${p.id}`}>{p.sessions_remaining}/{p.sessions_total} left</div>
    </div>
  );
}

function EventRow({ e, i }) {
  const { Icon, color } = EVENT[e.kind] || EVENT.purchase;
  const amt = e.kind === "used" ? "−1" : ["purchase", "gift"].includes(e.kind) ? (e.amount ? `+${e.amount}` : "") : e.amount === 0 ? "" : "+1";
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#1F1F1F]" data-testid={`credit-event-${i}`} data-kind={e.kind}>
      <span className="w-7 h-7 shrink-0 flex items-center justify-center" style={{ background: `${color}22`, color }}><Icon className="w-3.5 h-3.5" /></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">{e.label}{e.session_title ? ` — ${e.session_title}` : ""}{e.session_cancelled ? " (session cancelled)" : ""}</div>
        <div className="text-xs text-[#A3A3A3]">
          {[e.session_start && `Session ${fmtDT(e.session_start)}`, e.reason && e.reason !== e.label && `Reason: ${e.reason}`, e.pack_name].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-sm" style={{ color }}>{amt}</div>
        <div className="text-[11px] text-[#737373]">{fmtDate(e.at)}</div>
      </div>
    </div>
  );
}

export default function MyCredits() {
  return <MyCreditsPanel />;
}

export function MyCreditsPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/me/credits").then((r) => setD(r.data)).catch(() => setD({ total_remaining: 0, all_packs: [], activity: [] })); }, []);
  const packCtl = useListControls(d?.all_packs, { name: (p) => p.name, initialSort: "default" });
  const actCtl = useListControls(d?.activity, { name: (e) => e.label || e.title || e.description || "", fields: (e) => [e.label, e.title, e.description, e.pack_name], initialSort: "default" });
  if (!d) return <div className="p-8 text-sm text-[#A3A3A3] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading credits…</div>;
  return (
    <div className="max-w-3xl space-y-6" data-testid="my-credits-page">
      <div className="card-surface p-6 flex items-center gap-5" style={{ borderLeft: "3px solid #CCFF00" }}>
        <div className="w-14 h-14 flex items-center justify-center bg-[#CCFF00] text-black"><Coins className="w-7 h-7" /></div>
        <div>
          <div className="label-mono">MY CREDITS</div>
          <div className="h-display text-5xl leading-none mt-1" data-testid="my-credits-total">{d.total_remaining}</div>
          <div className="text-xs text-[#A3A3A3] mt-1">{d.total_remaining === 0 ? "You have no credits right now" : d.next_expiry ? `Next expiry ${fmtDate(d.next_expiry)}` : "credits available"}</div>
        </div>
      </div>
      <div className="card-surface p-5" data-testid="my-credits-packs">
        <div className="label-mono mb-2">CREDIT PACKS</div>
        {d.all_packs.length > 1 && <ListControlsBar ctl={packCtl} modes={["default", "az", "za"]} placeholder="Search packs…" testid="credit-packs" className="mb-2" />}
        {d.all_packs.length ? packCtl.list.map((p) => <PackRow key={p.id} p={p} />) : <div className="text-sm text-[#737373]">No credit packs yet.</div>}
      </div>
      <div className="card-surface p-5" data-testid="my-credits-activity">
        <div className="label-mono mb-2">ACTIVITY</div>
        {d.activity.length > 1 && <ListControlsBar ctl={actCtl} modes={["default", "az", "za"]} placeholder="Search activity…" testid="credit-activity" className="mb-2" />}
        {d.activity.length ? actCtl.list.map((e, i) => <EventRow key={i} e={e} i={i} />) : <div className="text-sm text-[#737373]">No credit activity yet.</div>}
      </div>
      <Link to="/offerings" onClick={() => { try { localStorage.setItem("rl_offerings_tab", "packages"); } catch (_e) { /* silent */ } }}
        className="btn-volt px-5 py-3 text-sm inline-flex items-center gap-2" data-testid="my-credits-buy">
        <ShoppingBag className="w-4 h-4" /> Buy more credits
      </Link>
    </div>
  );
}
